import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import { eventService } from "@/services/event.service";
import { useAuth } from "@/contexts/AuthContext";

const vendorCategories = [
  "Catering",
  "Photography & Video",
  "Decoration",
  "Music & Entertainment",
  "Makeup & Beauty",
  "Venue",
  "Hotels",
  "Transportation",
  "Invitations",
  "Other"
];

export const AddVendorDialog = ({ open, onOpenChange, onAddVendor, weddingId }) => {
  const { activeOrg } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    contact: "",
    email: "",
    quotedAmount: "",
    negotiatedAmount: "",
    advancePaid: "0",
    status: "negotiating",
    location: null,
    events: [],
    providesAccommodation: false,
    roomsBooked: ""
  });
  const [availableEvents, setAvailableEvents] = useState([]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!open || !weddingId || !activeOrg?.id) return;
      try {
        const data = await eventService.getEvents(weddingId, activeOrg.id);
        setAvailableEvents(data || []);
      } catch (error) {
        console.error("Failed to load events for vendor form:", error);
        setAvailableEvents([]);
      }
    };

    fetchEvents();
  }, [open, weddingId]);

  // Reset accommodation state when category changes to non-accommodation categories
  useEffect(() => {
    // Only "Venue" and "Hotels" categories can provide accommodation
    const canProvideAccommodation = ["Venue", "Hotels"].includes(formData.category);
    
    if (formData.category && !canProvideAccommodation && formData.providesAccommodation) {
      setFormData(prev => ({
        ...prev,
        providesAccommodation: false,
        roomsBooked: ""
      }));
    }
  }, [formData.category]);

  const handleEventToggle = (eventName) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventName)
        ? prev.events.filter(e => e !== eventName)
        : [...prev.events, eventName]
    }));
  };

  const handleLocationChange = (place) => {
    setFormData({ ...formData, location: place });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Location is optional
    if (!formData.name || !formData.category || !formData.contact || !formData.email || !formData.quotedAmount || !formData.negotiatedAmount) {
      toast.error("Please fill in all required fields (location is optional)");
      return;
    }

    // Events are only required if status is "confirmed"
    if (formData.status === "confirmed" && formData.events.length === 0) {
      if ((availableEvents || []).length === 0) {
        toast.error("No events added in timeline. Please add events in the Timeline tab first.");
      } else {
        toast.error("Please select at least one event for confirmed vendors");
      }
      return;
    }

    // Format location data for backend - preserve ALL fields from Google Places
    const locationData = formData.location ? {
      address: formData.location.address || formData.location.name,
      lat: formData.location.location?.lat,
      lng: formData.location.location?.lng,
      photo_reference: formData.location.photoReference || null,  // Store photo_reference instead of photo_url
      place_id: formData.location.placeId || null
    } : null;
    
    // Debug: Log location data to check photo_reference
    console.log('Creating vendor with location:', locationData);

    const newVendor = {
      name: formData.name,
      category: formData.category,
      contact_person: formData.name,
      phone: formData.contact,
      email: formData.email,
      quoted: parseFloat(formData.quotedAmount),
      negotiated: parseFloat(formData.negotiatedAmount),
      advance_paid: parseFloat(formData.advancePaid) || 0,
      status: formData.status,
      location: locationData,
      events: formData.events,
      provides_accommodation: formData.category === "Hotels" ? true : formData.providesAccommodation,
      rooms_booked: (formData.category === "Hotels" || formData.providesAccommodation) && formData.roomsBooked ? parseInt(formData.roomsBooked) : null,
      notes: null
    };

    onAddVendor(newVendor);
    toast.success(`Vendor ${formData.name} added successfully!`);
    
    // Reset form
    setFormData({
      name: "",
      category: "",
      contact: "",
      email: "",
      quotedAmount: "",
      negotiatedAmount: "",
      advancePaid: "0",
      status: "negotiating",
      location: null,
      events: [],
      providesAccommodation: false,
      roomsBooked: ""
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-2xl font-bold gradient-text">Add New Vendor</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="space-y-3 overflow-y-auto pr-2 flex-1">
            <div>
              <Label htmlFor="vendorName">Vendor Name *</Label>
              <Input
                id="vendorName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter vendor name"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {vendorCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="location">Location (Optional)</Label>
              <div className="mt-1.5">
                <GooglePlacesAutocomplete
                  value={formData.location}
                  onChange={handleLocationChange}
                  placeholder="Search for vendor location..."
                  types={['establishment']}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact">Contact Number *</Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  placeholder="+91 XXXXX XXXXX"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="vendor@email.com"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quoted">Quoted Amount (₹) *</Label>
                <Input
                  id="quoted"
                  type="number"
                  value={formData.quotedAmount}
                  onChange={(e) => setFormData({ ...formData, quotedAmount: e.target.value })}
                  placeholder="100000"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="negotiated">Negotiated Amount (₹) *</Label>
                <Input
                  id="negotiated"
                  type="number"
                  value={formData.negotiatedAmount}
                  onChange={(e) => setFormData({ ...formData, negotiatedAmount: e.target.value })}
                  placeholder="90000"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="advancePaid">Total Paid (₹)</Label>
                <Input
                  id="advancePaid"
                  type="number"
                  value={formData.advancePaid}
                  onChange={(e) => setFormData({ ...formData, advancePaid: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Accommodation Options - Only show for categories that can provide accommodation */}
            {formData.category && ["Venue"].includes(formData.category) && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="providesAccommodation"
                  checked={formData.providesAccommodation}
                  onCheckedChange={(checked) => setFormData({ ...formData, providesAccommodation: checked })}
                />
                <Label htmlFor="providesAccommodation" className="text-sm cursor-pointer">
                  This vendor also provides accommodation
                </Label>
              </div>
            )}

            {/* Show rooms field for Hotels OR if provides accommodation is checked */}
            {(formData.category === "Hotels" || formData.providesAccommodation) && (
              <div>
                <Label htmlFor="roomsBooked">Number of Rooms Available/Booked</Label>
                <Input
                  id="roomsBooked"
                  type="number"
                  value={formData.roomsBooked}
                  onChange={(e) => setFormData({ ...formData, roomsBooked: e.target.value })}
                  placeholder="10"
                  className="mt-1.5"
                />
              </div>
            )}

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="negotiating">Negotiating</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">
                Events {formData.status === "confirmed" && <span className="text-destructive">*</span>}
                <span className="text-xs text-muted-foreground ml-1">(Required only for confirmed vendors)</span>
              </Label>
              {availableEvents.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No events added in the timeline yet. Please add events in the Timeline tab before assigning them to vendors.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableEvents.map((event) => (
                    <div key={event.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`vendor-event-${event.id}`}
                        checked={formData.events.includes(event.name)}
                        onCheckedChange={() => handleEventToggle(event.name)}
                      />
                      <Label htmlFor={`vendor-event-${event.id}`} className="cursor-pointer">{event.name}</Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="gradient-primary">
              Add Vendor
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddVendorDialog;
